import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Enhanced Search Top Bar
const SearchPageTopBar = ({
    hotelCount = 120,
    showSortSheet,
    setShowSortSheet,
    setShowFilter,
    sortBy,
    setSortBy,
    city = "Ujjain",
    isSearching = false
}) => {

    const navigate = useNavigate();
    const shortCity = city ? city.split(',')[0].trim() : "";

    return (
        <>
            {/* Desktop Header */}
            <div className="hidden lg:flex justify-between items-center mb-1 p-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isSearching ? (
                            <>Finding Hotels in {shortCity && <span className="text-blue-600"> {shortCity}</span>}</>
                        ) : (
                            <>{hotelCount} Hotels Found in {shortCity && <span className="text-blue-600"> {shortCity}</span>}</>
                        )}
                    </h1>
                    <p className="text-gray-600 mt-1">Great deals on hotels in your destination</p>
                </div>

                <div className="flex items-center gap-3">
                    <label className="text-sm font-medium text-gray-700">Sort by</label>
                    <select
                        className="px-2 py-2 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                    >
                        <option value="price-asc">Price: Low to High</option>
                        <option value="price-desc">Price: High to Low</option>
                        <option value="rating">Guest Rating</option>
                        <option value="popular">Most Popular</option>
                    </select>
                </div>
            </div>

            {/* Mobile Header */}
            <div className="lg:hidden bg-white sticky top-0 z-40 border-b border-gray-200">
                <div className="py-2 px-2">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <button className="p-2 -ml-2" onClick={()=>navigate(-1)}>
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>

                            <div className="flex text-center">
                                <h1 className="text-lg font-bold text-gray-900 truncate max-w-[200px] sm:max-w-xs">
                                    {isSearching ? `Searching in ${shortCity}...` : `${hotelCount} Hotels in ${shortCity}`}
                                </h1>
                            </div>
                        </div>

                        <div className="flex items-center space-x-0.5 -mr-1">
                            <button className="p-2 text-gray-600" onClick={() => setShowSortSheet(true)}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" />
                                </svg>
                            </button>
                            <button className="p-2 text-gray-600" onClick={() => setShowFilter && setShowFilter(true)}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                                </svg>
                            </button>
                            <button className="p-2 text-gray-600" onClick={()=>navigate('/explore')}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Sort Bottom Sheet */}
            {showSortSheet && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 lg:hidden">
                    <div
                        className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl"
                        style={{
                            animation: "slideUp 0.3s ease-out"
                        }}
                    >
                        {/* Handle bar */}
                        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mt-3 mb-4"></div>

                        {/* Header */}
                        <div className="flex justify-between items-center px-6 pb-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-900">Sort By</h2>
                            <button
                                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
                                onClick={() => setShowSortSheet(false)}
                            >
                                <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Sort Options */}
                        <div className="px-6 py-4">
                            {[
                                { value: "price-asc", label: "Price: Low to High", icon: "M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" },
                                { value: "price-desc", label: "Price: High to Low", icon: "M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4" },
                                { value: "rating", label: "Guest Rating", icon: "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" },
                                { value: "popular", label: "Most Popular", icon: "M13 10V3L4 14h7v7l9-11h-7z" }
                            ].map(({ value, label, icon }) => (
                                <button
                                    key={value}
                                    className={`w-full flex items-center justify-between p-4 rounded-xl mb-2 transition-all duration-200 ${sortBy === value
                                        ? "bg-blue-50 border-2 border-blue-200 text-blue-700"
                                        : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                                        }`}
                                    onClick={() => {
                                        setSortBy(value);
                                        setShowSortSheet(false);
                                    }}
                                >
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                                        </svg>
                                        <span className="font-medium">{label}</span>
                                    </div>
                                    {sortBy === value && (
                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #3B82F6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        
        .line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }
        
        .active\\:scale-98:active {
          transform: scale(0.98);
        }
      `}</style>
        </>
    );
};

export default SearchPageTopBar;
